import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  createOrganization,
  updateOrganizationName,
} from "@/api/organizations";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  clearQueuedPostSignupApiKeyPrompt,
  queuePostSignupApiKeyPrompt,
} from "@/lib/post-signup-api-key-prompt";
import { queryKeys } from "@/lib/query-keys";
import { useOrganizationStore } from "@/store/organization";

const formSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: "Organization name must be at least 2 characters." })
    .max(100, {
      message: "Organization name must be at most 100 characters.",
    }),
});

type FormValues = z.infer<typeof formSchema>;

/**
 * Renders the create-or-edit organization step. Creation persists the org,
 * selects its WorkOS context, and invalidates the organization query; selection
 * failure retains the created org for a switch-only retry. Active organizations
 * leave NUX through the parent status gate, while legacy pending organizations
 * continue to pricing. Rename updates the canonical org, Zustand state, and
 * query cache. Errors remain inline, and continuation occurs only after the
 * relevant synchronization completes.
 *
 * @param onContinue - Advances a legacy pending organization to pricing.
 * @param onBack - Returns to the welcome step without saving.
 * @returns The organization form with create, rename, retry, and navigation states.
 */
export const OrganizationStep = ({
  onContinue,
  onBack,
}: {
  onContinue: () => void;
  onBack: () => void;
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createdOrganization, setCreatedOrganization] = useState<Awaited<
    ReturnType<typeof createOrganization>
  > | null>(null);
  const [isCompletingSelection, setIsCompletingSelection] = useState(false);
  const organization = useOrganizationStore((s) => s.organization);
  const organizations = useOrganizationStore((s) => s.organizations);
  const setOrganization = useOrganizationStore((s) => s.setOrganization);
  const setOrganizationState = useOrganizationStore(
    (s) => s.setOrganizationState,
  );
  const isSwitching = useOrganizationStore((s) => s.isSwitching);
  const queryClient = useQueryClient();
  const isEditing = !!organization;

  const completeOrganizationSelection = async (
    organization: Awaited<ReturnType<typeof createOrganization>>,
  ) => {
    setIsCompletingSelection(true);
    queuePostSignupApiKeyPrompt();
    try {
      await setOrganization(organization);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all(),
      });
      setCreatedOrganization(null);
      if (organization.account_status === "pending_approval") {
        onContinue();
      }
    } catch {
      clearQueuedPostSignupApiKeyPrompt();
      setCreatedOrganization(organization);
      setErrorMessage(
        "Organization created, but we could not switch to it. Please try again.",
      );
    } finally {
      setIsCompletingSelection(false);
    }
  };

  const saveOrganizationMutation = useMutation({
    mutationFn: async (name: string) => {
      if (organization) {
        return updateOrganizationName(organization.id, name);
      }
      return createOrganization(name, { selfServeBillingEnabled: true });
    },
    onSuccess: async (savedOrganization) => {
      if (organization) {
        await queryClient.cancelQueries({
          queryKey: queryKeys.organizations.all(),
        });
        const updatedOrganizations = organizations.some(
          (candidate) => candidate.id === savedOrganization.id,
        )
          ? organizations.map((candidate) =>
              candidate.id === savedOrganization.id
                ? savedOrganization
                : candidate,
            )
          : [...organizations, savedOrganization];
        setOrganizationState({
          organization: savedOrganization,
          organizations: updatedOrganizations,
        });
        queryClient.setQueryData(
          queryKeys.organizations.all(),
          updatedOrganizations,
        );
      } else {
        await completeOrganizationSelection(savedOrganization);
        return;
      }

      onContinue();
    },
    onError: (_err) => {
      setErrorMessage(
        `Failed to ${isEditing ? "update" : "create"} organization. Please try again.`,
      );
    },
  });

  const isLoading =
    saveOrganizationMutation.isPending || isSwitching || isCompletingSelection;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: organization?.name ?? "" },
  });

  const onSubmit = (values: FormValues) => {
    setErrorMessage(null);
    saveOrganizationMutation.mutate(values.name);
  };

  return (
    <>
      <div>
        <h2 className="text-[32px] font-medium leading-tight tracking-tight">
          {isEditing ? "Edit Your Organization" : "Create Your Organization"}
        </h2>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          Your organization owns your API keys, usage, and billing.
        </p>
      </div>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  Organization Name
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Acme Inc."
                    {...field}
                    autoFocus
                    disabled={isLoading || !!createdOrganization}
                    className="h-12 bg-background text-base shadow-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-wrap justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="h-11 min-w-24 px-5"
              disabled={isLoading || !!createdOrganization}
              onClick={onBack}
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back
            </Button>
            <Button
              type={createdOrganization ? "button" : "submit"}
              className="group h-11 min-w-56 px-6 text-sm font-medium shadow-none"
              disabled={isLoading}
              size="lg"
              onClick={
                createdOrganization
                  ? () =>
                      void completeOrganizationSelection(createdOrganization)
                  : undefined
              }
            >
              {isLoading ? (
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
              ) : createdOrganization ? (
                "Retry Organization Switch"
              ) : isEditing ? (
                "Save & Continue"
              ) : (
                "Create Organization"
              )}
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};
